import { type MouseEvent as ReactMouseEvent, useEffect, useMemo, useState } from "react"
import {
  Globe,
  RefreshCw,
  Search,
  X,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  AlertTriangle,
  ExternalLink,
  Building2,
  FileText,
  ShieldAlert,
  Layers,
  Image as ImageIcon,
  Link2,
  Clock,
} from "lucide-react"
import {
  getStudioSiteOverview,
  getStudioSitePages,
  getStudioSitePage,
  type StudioSiteOverview,
  type StudioSiteOverviewStudio,
  type StudioSitePagesDetail,
  type StudioSitePageRow,
  type StudioSitePageFull,
} from "@/lib/api"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { KpiCard } from "@/components/KpiCard"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"

const STATUS_COLOR: Record<string, string> = {
  succeeded: "bg-emerald-100 text-emerald-800",
  running: "bg-blue-100 text-blue-800",
  capped: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  skipped_aggregator: "bg-gray-100 text-gray-700",
}

const STATUS_LABEL: Record<string, string> = {
  succeeded: "Succeeded",
  running: "Running",
  capped: "Capped",
  failed: "Failed",
  skipped_aggregator: "Aggregator",
}

const WALL_REASON_LABEL: Record<string, string> = {
  login_form: "Login form",
  login_url: "Login URL",
  aggregator_seed: "Aggregator seed",
}

type SortKey = "name" | "activity_type" | "origin" | "status" | "page_count" | "walls" | "last_crawl_at"

function timeAgo(iso: string | null): string {
  if (!iso) return "never"
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return "unknown"
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (secs < 60) return `${secs}s ago`
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "-"
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  return new Date(t).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function formatChars(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0"
  if (n < 1000) return `${n}`
  return `${(n / 1000).toFixed(1)}k`
}

function shortUrl(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/\/$/, "")
}

export function StudioInfoPage() {
  const [data, setData] = useState<StudioSiteOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState("")
  const [sortKey, setSortKey] = useState<SortKey>("page_count")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")
  const [selected, setSelected] = useState<StudioSiteOverviewStudio | null>(null)
  const [drawerWidth, setDrawerWidth] = useState<number>(() =>
    typeof window !== "undefined" ? Math.round(window.innerWidth / 2) : 640,
  )

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const overview = await getStudioSiteOverview()
      setData(overview)
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load studio information"
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir(key === "name" || key === "activity_type" || key === "origin" ? "asc" : "desc")
    }
  }

  const filtered = useMemo(() => {
    const studios = data?.studios ?? []
    const q = search.trim().toLowerCase()
    if (!q) return studios
    return studios.filter(
      (s) =>
        (s.name ?? "").toLowerCase().includes(q) ||
        (s.origin ?? "").toLowerCase().includes(q) ||
        (s.url ?? "").toLowerCase().includes(q) ||
        (s.activity_type ?? "").toLowerCase().includes(q),
    )
  }, [data, search])

  const sorted = useMemo(() => {
    const valueOf = (s: StudioSiteOverviewStudio): string | number => {
      switch (sortKey) {
        case "page_count":
          return s.page_count
        case "walls":
          return s.last_crawl?.walls_hit ?? 0
        case "status":
          return (s.last_crawl?.status ?? "").toLowerCase()
        case "activity_type":
          return (s.activity_type ?? "").toLowerCase()
        case "origin":
          return (s.origin ?? "").toLowerCase()
        case "last_crawl_at":
          return s.last_crawl?.finished_at ?? s.last_crawl?.started_at ?? ""
        default:
          return (s.name ?? "").toLowerCase()
      }
    }
    const dir = sortDir === "asc" ? 1 : -1
    return [...filtered].sort((a, b) => {
      const av = valueOf(a)
      const bv = valueOf(b)
      let cmp: number
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv
      else cmp = String(av).localeCompare(String(bv))
      // Keep active studios above inactive within a tie, then name.
      if (cmp === 0) cmp = Number(b.is_active) - Number(a.is_active)
      if (cmp === 0) cmp = (a.name ?? "").toLowerCase().localeCompare((b.name ?? "").toLowerCase())
      return cmp * dir
    })
  }, [filtered, sortKey, sortDir])

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Globe className="size-6 text-muted-foreground" /> Studio Information
        </h1>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Globe className="size-6 text-muted-foreground" /> Studio Information
        </h1>
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
          <AlertTriangle className="mx-auto mb-2 size-8 text-destructive" />
          <p className="text-sm text-destructive">{error ?? "No data"}</p>
          <button
            onClick={() => void load()}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
          >
            <RefreshCw className="size-3.5" /> Retry
          </button>
        </div>
      </div>
    )
  }

  const o = data.overall

  return (
    <div
      className="space-y-6 p-6 transition-[padding] duration-150"
      style={selected ? { paddingRight: drawerWidth + 24 } : undefined}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Globe className="size-6" /> Studio Information
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Raw site-crawler data per studio: how many pages were crawled from each
            studio's public website, which pages hit a login/booking wall, and the
            captured text and screenshots.{" "}
            <span title={formatDateTime(data.generated_at)}>
              Updated {timeAgo(data.generated_at)}.
            </span>
          </p>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
        >
          <RefreshCw className={cn("size-3.5", loading && "animate-spin")} /> Refresh
        </button>
      </div>

      {!data.provisioned ? (
        <NotProvisionedNote />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <KpiCard
              title="Studios crawled"
              value={`${o.studios_crawled}/${o.studios_with_url}`}
              icon={Building2}
              subtitle={`${o.studios_total} total · ${o.studios_with_url} with a URL`}
            />
            <KpiCard title="Pages crawled" value={o.pages_total} icon={FileText} />
            <KpiCard
              title="Walls hit"
              value={o.walls_total}
              icon={ShieldAlert}
              subtitle="login / booking walls recorded"
              variant={o.walls_total > 0 ? "warning" : "default"}
            />
            <KpiCard
              title="Screenshots"
              value={o.screenshots_total}
              icon={ImageIcon}
              subtitle={
                o.last_crawl_at ? `last crawl ${timeAgo(o.last_crawl_at)}` : "no crawls yet"
              }
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[240px] flex-1">
              <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search name, origin, URL, activity..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              {sorted.length} of {data.studios.length} studios
            </span>
            {search.trim() !== "" && (
              <button
                onClick={() => setSearch("")}
                className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs hover:bg-accent"
              >
                <X className="size-3" /> Clear
              </button>
            )}
          </div>

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="w-8 px-2 py-2" />
                  <SortHeader label="Name" col="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Activity" col="activity_type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Origin" col="origin" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Last crawl" col="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Pages" col="page_count" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <SortHeader label="Walls" col="walls" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <th className="px-3 py-2 text-center font-medium">Capped</th>
                  <SortHeader label="Crawled" col="last_crawl_at" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {sorted.map((s) => (
                  <StudioRow key={s.id} studio={s} onSelect={() => setSelected(s)} />
                ))}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                      {data.studios.length === 0
                        ? "No studios found."
                        : "No studios match your search."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Studio detail panel (non-modal split view; list stays scrollable) */}
      <StudioInfoDrawer
        studio={selected}
        onClose={() => setSelected(null)}
        width={drawerWidth}
        onWidth={setDrawerWidth}
      />
    </div>
  )
}

// --- not-provisioned empty state ---------------------------------------------

function NotProvisionedNote() {
  return (
    <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center">
      <Globe className="mx-auto mb-3 size-8 text-muted-foreground" />
      <p className="text-sm font-medium">Site crawler not provisioned yet</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        No crawl tables or data are present on this backend. Run the{" "}
        <span className="font-mono text-xs">siteinfo</span> crawler against staging to
        populate per-studio pages, text, and screenshots here.
      </p>
    </div>
  )
}

// --- studio table row ---------------------------------------------------------

function StudioRow({
  studio: s,
  onSelect,
}: {
  studio: StudioSiteOverviewStudio
  onSelect: () => void
}) {
  const lc = s.last_crawl
  const crawledAt = lc?.finished_at ?? lc?.started_at ?? null
  return (
    <tr
      className={cn("cursor-pointer border-t hover:bg-muted/30", !s.is_active && "opacity-50")}
      onClick={onSelect}
    >
      <td className="px-2 py-2 text-muted-foreground">
        <ChevronRight className="size-4" />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2 font-medium">
          {s.name}
          {!s.is_active && (
            <Badge className="bg-gray-100 font-normal text-gray-500">inactive</Badge>
          )}
        </div>
        {s.url && (
          <div className="max-w-[220px] truncate text-xs text-muted-foreground" title={s.url}>
            {shortUrl(s.url)}
          </div>
        )}
      </td>
      <td className="px-3 py-2 capitalize">{s.activity_type || <Dash />}</td>
      <td className="px-3 py-2 text-muted-foreground">
        {s.origin ? (
          <span className="max-w-[200px] truncate" title={s.origin}>
            {shortUrl(s.origin)}
          </span>
        ) : (
          <Dash />
        )}
      </td>
      <td className="px-3 py-2">
        {lc ? <StatusBadge status={lc.status} /> : <NeverBadge />}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{s.page_count}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        {lc && lc.walls_hit > 0 ? (
          <span className="text-amber-700" title={`${lc.walls_hit} walls hit`}>
            {lc.walls_hit}
          </span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </td>
      <td className="px-3 py-2 text-center">
        {lc?.pages_capped ? (
          <Badge className="bg-amber-100 font-normal text-amber-800">capped</Badge>
        ) : (
          <Dash />
        )}
      </td>
      <td
        className="whitespace-nowrap px-3 py-2 text-muted-foreground"
        title={crawledAt ?? undefined}
      >
        {timeAgo(crawledAt)}
      </td>
    </tr>
  )
}

// --- studio detail drawer -----------------------------------------------------

function StudioInfoDrawer({
  studio,
  onClose,
  width,
  onWidth,
}: {
  studio: StudioSiteOverviewStudio | null
  onClose: () => void
  width: number
  onWidth: (w: number) => void
}) {
  const [detail, setDetail] = useState<StudioSitePagesDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Drag the left edge to resize the panel (min 380px, max 90vw).
  function startResize(e: ReactMouseEvent) {
    e.preventDefault()
    const onMove = (ev: MouseEvent) => {
      const next = window.innerWidth - ev.clientX
      const max = window.innerWidth * 0.9
      onWidth(Math.max(380, Math.min(next, max)))
    }
    const onUp = () => {
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup", onUp)
      document.body.style.userSelect = ""
    }
    document.body.style.userSelect = "none"
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup", onUp)
  }

  useEffect(() => {
    if (!studio) return
    let cancelled = false
    setDetail(null)
    setError(null)
    setLoading(true)
    getStudioSitePages(studio.id)
      .then((d) => {
        if (!cancelled) setDetail(d)
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load studio pages")
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [studio])

  if (!studio) return null

  const shots = (detail?.pages ?? []).filter((p) => p.screenshot_url)

  return (
    // Non-modal fixed panel: no backdrop, so the studio list stays scrollable
    // and clickable. Clicking another row swaps this panel's content.
    <aside
      className="fixed right-0 top-0 z-40 flex h-screen flex-col border-l bg-background shadow-2xl"
      style={{ width }}
    >
      {/* drag handle to resize (left edge) */}
      <div
        onMouseDown={startResize}
        title="Drag to resize"
        className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-primary/20"
      />
      <div className="flex shrink-0 items-start justify-between gap-2 border-b p-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 truncate text-lg font-semibold">
            {studio.name}
            {!studio.is_active && (
              <Badge className="bg-gray-100 font-normal text-gray-500">inactive</Badge>
            )}
          </h2>
          <p className="truncate text-xs text-muted-foreground" title={studio.origin ?? undefined}>
            {studio.origin ? shortUrl(studio.origin) : "Site information"}
          </p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* min-h-0 is required so the flex child can shrink and overflow-y engages. */}
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain p-4">
        {loading && (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-md border bg-muted" />
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {detail && !loading && (
          <>
            <CrawlMetaSection studio={studio} detail={detail} />
            <ScreenshotsSection shots={shots} />
            <PagesSection detail={detail} />
          </>
        )}
      </div>
    </aside>
  )
}

function CrawlMetaSection({
  studio,
  detail,
}: {
  studio: StudioSiteOverviewStudio
  detail: StudioSitePagesDetail
}) {
  const latest = detail.crawls[0] ?? null
  return (
    <section>
      <SectionTitle icon={Building2}>Crawl details</SectionTitle>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <KV label="Seed URL">
          {studio.url ? (
            <a
              href={studio.url}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1 break-all text-primary hover:underline"
            >
              {shortUrl(studio.url)}
              <ExternalLink className="size-3.5 shrink-0" />
            </a>
          ) : (
            <Dash />
          )}
        </KV>
        <KV label="Origin">
          <span className="break-all">{studio.origin || "-"}</span>
        </KV>
        <KV label="Pages crawled">
          <span className="tabular-nums">{detail.page_count}</span>
        </KV>
        <KV label="Last status">
          {latest ? <StatusBadge status={latest.status} /> : <NeverBadge />}
        </KV>
        <KV label="Walls hit">
          <span className="tabular-nums">{latest?.walls_hit ?? 0}</span>
        </KV>
        <KV label="Capped">
          {latest?.pages_capped ? (
            <Badge className="bg-amber-100 font-normal text-amber-800">yes</Badge>
          ) : (
            <span className="text-muted-foreground">no</span>
          )}
        </KV>
        <KV label="Caps (pages / depth)">
          <span className="tabular-nums">
            {latest ? `${latest.max_pages ?? "-"} / ${latest.max_depth ?? "-"}` : "-"}
          </span>
        </KV>
        <KV label="Finished">{formatDateTime(latest?.finished_at ?? null)}</KV>
      </div>

      {latest?.error && (
        <div className="mt-3 rounded-md border border-destructive/50 bg-destructive/5 p-2 text-xs text-destructive">
          {latest.error}
        </div>
      )}

      {detail.crawls.length > 0 && (
        <div className="mt-4">
          <div className="mb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            Recent crawls
          </div>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Started</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 text-right font-medium">Pages</th>
                  <th className="px-3 py-2 text-right font-medium">Walls</th>
                  <th className="px-3 py-2 text-center font-medium">Capped</th>
                </tr>
              </thead>
              <tbody>
                {detail.crawls.map((c) => (
                  <tr key={c.id} className="border-t">
                    <td className="whitespace-nowrap px-3 py-2" title={c.started_at ?? undefined}>
                      {formatDateTime(c.started_at)}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.pages_crawled}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.walls_hit}</td>
                    <td className="px-3 py-2 text-center">
                      {c.pages_capped ? (
                        <Badge className="bg-amber-100 font-normal text-amber-800">yes</Badge>
                      ) : (
                        <Dash />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  )
}

function ScreenshotsSection({ shots }: { shots: StudioSitePageRow[] }) {
  if (shots.length === 0) return null
  return (
    <section>
      <SectionTitle icon={ImageIcon}>Screenshots ({shots.length})</SectionTitle>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {shots.map((p) => (
          <a
            key={p.id}
            href={p.screenshot_url ?? undefined}
            target="_blank"
            rel="noopener"
            title={`Open screenshot of ${p.title || shortUrl(p.url)}`}
            className="group block overflow-hidden rounded-md border hover:border-primary"
          >
            <img
              src={p.screenshot_url ?? undefined}
              alt={`Screenshot of ${p.title || p.url}`}
              loading="lazy"
              className="h-40 w-full bg-muted object-cover object-top transition-opacity group-hover:opacity-90"
            />
            <div
              className="truncate px-2 py-1 text-[11px] text-muted-foreground"
              title={p.url}
            >
              {p.title || shortUrl(p.url)}
            </div>
          </a>
        ))}
      </div>
    </section>
  )
}

function PagesSection({ detail }: { detail: StudioSitePagesDetail }) {
  if (detail.pages.length === 0) {
    return (
      <section>
        <SectionTitle icon={FileText}>Pages</SectionTitle>
        <div className="rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
          No pages crawled yet for this studio.
        </div>
      </section>
    )
  }
  return (
    <section>
      <SectionTitle icon={FileText}>Pages ({detail.page_count})</SectionTitle>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="w-8 px-2 py-2" />
              <th className="px-3 py-2 text-right font-medium">Depth</th>
              <th className="px-3 py-2 font-medium">Title / URL</th>
              <th className="px-3 py-2 font-medium">Wall</th>
              <th className="px-3 py-2 text-right font-medium">Links</th>
              <th className="px-3 py-2 text-right font-medium">Text</th>
            </tr>
          </thead>
          <tbody>
            {detail.pages.map((p) => (
              <PageRow key={p.id} page={p} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// --- expandable page row (fetches full text on demand) -----------------------

function PageRow({ page: p }: { page: StudioSitePageRow }) {
  const [expanded, setExpanded] = useState(false)
  const [full, setFull] = useState<StudioSitePageFull | null>(null)
  const [loadingFull, setLoadingFull] = useState(false)
  const [fullError, setFullError] = useState<string | null>(null)

  function toggle() {
    const next = !expanded
    setExpanded(next)
    // Lazy-load the full text the first time the row is opened.
    if (next && !full && !loadingFull) {
      setLoadingFull(true)
      setFullError(null)
      getStudioSitePage(p.id)
        .then(setFull)
        .catch((e) =>
          setFullError(e instanceof Error ? e.message : "Failed to load page text"),
        )
        .finally(() => setLoadingFull(false))
    }
  }

  return (
    <>
      <tr className="cursor-pointer border-t align-top hover:bg-muted/30" onClick={toggle}>
        <td className="px-2 py-2 text-muted-foreground">
          {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{p.depth}</td>
        <td className="px-3 py-2">
          <div className="font-medium">{p.title || <span className="text-muted-foreground">(untitled)</span>}</div>
          <div className="max-w-[320px] truncate text-xs text-muted-foreground" title={p.url}>
            {shortUrl(p.url)}
          </div>
        </td>
        <td className="px-3 py-2">
          {p.is_wall ? (
            <Badge
              className="bg-amber-100 font-normal text-amber-800"
              title={p.wall_reason ?? undefined}
            >
              {p.wall_reason ? WALL_REASON_LABEL[p.wall_reason] ?? p.wall_reason : "wall"}
            </Badge>
          ) : (
            <Dash />
          )}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{p.link_count}</td>
        <td
          className="px-3 py-2 text-right tabular-nums text-muted-foreground"
          title={`${p.text_length} chars`}
        >
          {formatChars(p.text_length)}
        </td>
      </tr>
      {expanded && (
        <tr className="border-t bg-muted/20">
          <td />
          <td colSpan={5} className="px-3 py-3">
            <PageExpanded page={p} full={full} loading={loadingFull} error={fullError} />
          </td>
        </tr>
      )}
    </>
  )
}

function PageExpanded({
  page: p,
  full,
  loading,
  error,
}: {
  page: StudioSitePageRow
  full: StudioSitePageFull | null
  loading: boolean
  error: string | null
}) {
  const headings = full?.headings ?? p.headings ?? []
  const text = full?.text_content ?? p.text_preview ?? ""
  const aria = full?.aria_snapshot ?? ""
  const links = full?.links ?? []
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <a
          href={p.url}
          target="_blank"
          rel="noopener"
          className="inline-flex items-center gap-1 break-all text-primary hover:underline"
        >
          {p.url}
          <ExternalLink className="size-3 shrink-0" />
        </a>
        {p.http_status != null && <span className="tabular-nums">HTTP {p.http_status}</span>}
        <span className="inline-flex items-center gap-1">
          <Link2 className="size-3" /> {p.link_count} {p.link_count === 1 ? "link" : "links"}
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock className="size-3" /> crawled {timeAgo(p.last_crawled_at)}
        </span>
      </div>

      {p.error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-2 text-xs text-destructive">
          {p.error}
        </div>
      )}

      {headings.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {headings.map((h, i) => (
            <span
              key={`${h.tag}-${i}`}
              className="rounded border bg-background px-1.5 py-0.5 text-[11px]"
              title={h.tag}
            >
              {h.text}
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="h-24 animate-pulse rounded-md border bg-muted" />
      ) : error ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-2 text-xs text-destructive">
          {error}
        </div>
      ) : text ? (
        <div>
          <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            <FileText className="size-3" />
            {full ? "Full text" : "Preview"}
            <span className="tabular-nums normal-case">({formatChars(p.text_length)} chars)</span>
          </div>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap wrap-break-word rounded-md border bg-background p-2 text-xs leading-relaxed">
            {text}
          </pre>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No text captured for this page.</p>
      )}

      {aria && (
        <div>
          <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            <Layers className="size-3" /> Accessibility snapshot
            <span className="tabular-nums normal-case">({formatChars(aria.length)} chars)</span>
          </div>
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap wrap-break-word rounded-md border bg-background p-2 font-mono text-[11px] leading-relaxed">
            {aria}
          </pre>
        </div>
      )}

      {links.length > 0 && (
        <div>
          <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            <Layers className="size-3" /> Links ({links.length})
          </div>
          <div className="max-h-40 space-y-0.5 overflow-auto rounded-md border bg-background p-2 font-mono text-[11px]">
            {links.map((l, i) => (
              <div key={`${l}-${i}`} className="truncate text-muted-foreground" title={l}>
                {shortUrl(l)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// --- shared sub-components -----------------------------------------------------

function SortHeader({
  label,
  col,
  sortKey,
  sortDir,
  onSort,
  align = "left",
}: {
  label: string
  col: SortKey
  sortKey: SortKey
  sortDir: "asc" | "desc"
  onSort: (k: SortKey) => void
  align?: "left" | "right" | "center"
}) {
  const active = sortKey === col
  return (
    <th className="px-3 py-2 font-medium">
      <button
        onClick={() => onSort(col)}
        className={cn(
          "-mx-1 flex items-center gap-1 rounded px-1 py-0.5 hover:text-foreground",
          align === "right" && "ml-auto",
          align === "center" && "mx-auto",
          active && "text-foreground",
        )}
        title={`Sort by ${label}`}
      >
        {label}
        {active ? (
          sortDir === "asc" ? (
            <ChevronUp className="size-3.5" />
          ) : (
            <ChevronDown className="size-3.5" />
          )
        ) : (
          <ChevronsUpDown className="size-3.5 opacity-40" />
        )}
      </button>
    </th>
  )
}

function SectionTitle({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}) {
  return (
    <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
      <Icon className="size-4 text-muted-foreground" /> {children}
    </div>
  )
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="min-w-0 wrap-break-word text-sm">{children}</span>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge className={cn("font-normal", STATUS_COLOR[status] ?? "bg-gray-100 text-gray-700")}>
      {STATUS_LABEL[status] ?? status ?? "-"}
    </Badge>
  )
}

function NeverBadge() {
  return <Badge className="bg-gray-100 font-normal text-gray-500">never crawled</Badge>
}

function Dash() {
  return <span className="text-muted-foreground">-</span>
}
